// Lógica del dictado por voz del composer ("Crear desde cero"), testeable
// sin navegador: el constructor de SpeechRecognition entra inyectado. Lo que
// sí toca browser APIs de verdad (AnalyserNode, getUserMedia, canvas) vive
// en DictadoVoz.tsx.

export type TramoDictado = { texto: string; esFinal: boolean };

// --- Tipos mínimos de la Web Speech API (no está en lib.dom) ---------------

export type ResultadoReconocimiento = {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
};

export type ReconocimientoVoz = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: ResultadoReconocimiento) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
};

/**
 * Crea y arranca UNA sesión de dictado, encapsulando el ciclo de vida del
 * reconocimiento: Chrome corta la sesión ante silencios (`onend` espontáneo)
 * y acá se re-arranca sola, plegando los finales de la sesión muerta
 * (`results` arranca de cero por sesión).
 *
 * La cancelación es POR INSTANCIA (closure), nunca estado compartido:
 * `detener()` apaga la bandera, DESENGANCHA los handlers y aborta. Con un
 * ref compartido, el doble montaje de StrictMode dejaba dos sesiones vivas
 * re-arrancándose: el `onend` (asíncrono) del abort de la primera veía la
 * bandera que el segundo montaje ya había vuelto a prender — y como Chrome
 * banca UN solo reconocimiento activo, las dos se abortaban mutuamente en
 * loop y nunca llegaba un resultado.
 */
export function iniciarDictado(opciones: {
  Reconocimiento: new () => ReconocimientoVoz;
  lang: string;
  /** Cada actualización: `completo` incluye la interina; `final` no. */
  onTexto: (t: { completo: string; final: string }) => void;
  onErrorPermiso: () => void;
}): { detener: () => void } {
  let activo = true;
  let finalesPrevios = '';
  let final = '';

  const r = new opciones.Reconocimiento();
  r.lang = opciones.lang;
  r.continuous = true;
  r.interimResults = true;

  r.onresult = (e) => {
    const tramos: TramoDictado[] = [];
    for (let i = 0; i < e.results.length; i++) {
      const t = e.results[i];
      tramos.push({ texto: t[0].transcript, esFinal: t.isFinal });
    }
    const sesion = transcripcionDeSesion(tramos);
    final = combinarDictado(finalesPrevios, sesion.finales);
    opciones.onTexto({
      completo: combinarDictado(final, sesion.interina),
      final,
    });
  };
  r.onend = () => {
    if (!activo) return;
    // Sesión cortada por silencio: plegar lo final y arrancar otra.
    finalesPrevios = final;
    try {
      r.start();
    } catch {
      // start() sobre una sesión aún no liberada tira InvalidStateError;
      // el próximo onend la vuelve a intentar.
    }
  };
  r.onerror = (e) => {
    if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
      activo = false;
      opciones.onErrorPermiso();
    } else if (e.error && e.error !== 'no-speech' && e.error !== 'aborted') {
      // 'network' y compañía: el onend que les sigue re-arranca solo, pero
      // dejar rastro es la única forma de diagnosticarlos en el campo.
      console.warn('[dictado] error de reconocimiento:', e.error);
    }
  };
  r.start();

  return {
    detener() {
      activo = false;
      r.onresult = null;
      r.onend = null;
      r.onerror = null;
      r.abort();
    },
  };
}

/**
 * Reconstruye la transcripción de UNA sesión de SpeechRecognition a partir
 * de su lista completa de resultados (`event.results`). Se recalcula desde
 * cero en cada `onresult` — así no importa desde qué `resultIndex` llegó el
 * evento ni cuántas veces se re-emitió un tramo interino.
 */
export function transcripcionDeSesion(tramos: TramoDictado[]): {
  finales: string;
  interina: string;
} {
  return {
    finales: combinarDictado(
      ...tramos.filter((t) => t.esFinal).map((t) => t.texto),
    ),
    interina: combinarDictado(
      ...tramos.filter((t) => !t.esFinal).map((t) => t.texto),
    ),
  };
}

/**
 * Une tramos de texto con un solo espacio, ignorando vacíos. Se usa tanto
 * para armar el texto visible (sesiones previas + finales + interina) como
 * para el texto que se confirma al prompt.
 */
export function combinarDictado(...tramos: string[]): string {
  return tramos
    .map((t) => t.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ');
}

/**
 * Nivel de audio 0..1 a partir de las muestras time-domain del analyser
 * (bytes centrados en 128). RMS con una ganancia de 2: una voz normal no
 * llega ni cerca de la amplitud máxima, y sin ganancia el waveform queda
 * plano; ver DictadoVoz.tsx.
 */
export function nivelDesdeMuestras(muestras: Uint8Array): number {
  if (muestras.length === 0) return 0;
  let suma = 0;
  for (let i = 0; i < muestras.length; i++) {
    const v = (muestras[i] - 128) / 128;
    suma += v * v;
  }
  const rms = Math.sqrt(suma / muestras.length);
  return Math.min(1, rms * 2);
}
